import { ACCELERATOR_MODELS, APP_POOL_LABEL_VALUE_PATTERN, APP_POOL_NODE_GROUP_ID_PATTERN, APP_POOL_NODE_TYPE_MAX_LENGTH, APP_POOL_ORG_ID_PATTERN, APP_POOL_REGION_PATTERN, APP_POOL_TIER_FAMILY_MAX_LENGTH, APP_POOL_WORKLOADS, BOOT_DISK_TYPES, MAX_GPU_COUNT, POOL_NODE_COUNT_MAX, SCALING_INTENT_DISK_GIB_MAX, SCALING_INTENT_DISK_GIB_MIN, acceleratorEntry, acceleratorTypesFor, appPoolRegionSchema, appPoolSpotCapacitySchema, appPoolTierKeySchema, awsMachineTypeFamily, gcpMachineTypeFamily } from "@zion/sdk-contracts";
import { z } from "zod";
import { createHash } from "node:crypto";
//#region src/app-pool-node-group.ts
/**
* app-pools WS-2. The app-pool node-group wire contract: the compiled and clamped
* `AppPoolNodeGroupSpec`, plus the identity function both planes agree on.
*
* This node-pool array on the CP/DP wire sits beside the demand path. R2 of
* `2026-10-14-app-pools-runtime-deletion` removed `dedicatedNodePools` and `isolatedNodePools`
* (R-081). It follows the same three-hop write path that
* `research/pool-compiler-and-pod.md` §1 records. The operator compiles the array in memory and
* reports it on `StatusReport.appPoolNodeGroups`. release-engine persists it into
* `release.dp_live_status.app_pool_node_groups`. provisioning-svc recomputes the CP-owned fields
* and merge-PATCHes `spec.appPoolNodeGroups` on the DataPlane XR. The Composition then renders one
* GKE `NodePool` per item (WS-7, gcp only).
*
* What this file is for is chain D of `security-audit.md`: an out-of-mesh node never carries two
* orgs' attempts. The boundary is one identity function. An `out-of-mesh` group's id hashes the
* org, so two orgs on one app, one workload and one machine type resolve to two different
* `groupId`s. Two ids give two node pools, and therefore two disjoint sets of nodes, because the
* label and taint the Composition stamps carry the `groupId` as their value. An `in-mesh` group's
* id does not hash an org. An in-mesh pool runs the app's own code under the fixture's own
* identity. Packing it per org would buy nothing, and would cost one node pool per resident
* tenant (chain G hop 3).
*
* The `(workload, orgId)` pairing is therefore not a shape convenience. It is the single input
* that decides whether tenant separation exists at all, and {@link appPoolNodeGroupId} throws on
* an incoherent pair instead of picking a reading. See its docblock.
*/
/**
* Max app-pool node groups compiled per data plane, across every app and every org.
*
* This is a producer-side slice bound, not tidiness, and it is the same figure the removed
* `ISOLATED_POOL_MAX` used for the same reason (R2, R-081). `StatusReportSchema` is `.strict()`
* and is parsed as a whole. An over-cap array therefore fails the entire status report for every app on the data plane, and not
* only the offending group. The compiler slices to this bound and reports the truncation. It never
* emits an array the CP will refuse.
*/
const APP_POOL_NODE_GROUP_MAX = 100;
/**
* The pod-density values an app-pool node group may declare.
*
* This is a closed set and not a range, and the reason is arithmetic. GKE's per-node address block
* is the max-pods figure doubled and rounded up to a power of two. Every value in 17 to 32 costs a
* `/26` (64 addresses) and every value in 33 to 64 costs a `/25` (128). A `min` and `max` pair
* would admit 40 and 48, which buy nothing over 64 and cost the same block. If the answer is a
* `/25`, write 64.
*
* The removed `ISOLATED_POOL_MAX_PODS_PER_NODE_VALUES` (R2, R-081) declared the same closed set.
* This array was restated instead of imported because the two arrays answered for two different
* XRD arrays. WS-7 mirrors this one by hand into
* `spec.appPoolNodeGroups.items.properties.maxPodsPerNode.enum`.
*/
const APP_POOL_NODE_GROUP_MAX_PODS_PER_NODE_VALUES = [32, 64];
/** The density the CP writes when a group declares none (DEV-18, WS-3). 32 is the XRD's own
*  derivation for an isolated node pool: a 13-pod system floor plus 19 tenant slots, 3.37 addresses
*  per tenant pod against 4.74 at 64, and it suits every machine type in the catalog. A member of
*  {@link APP_POOL_NODE_GROUP_MAX_PODS_PER_NODE_VALUES}; the spec test pins that. */
const APP_POOL_MAX_PODS_PER_NODE_DEFAULT = 32;
/**
* The zone grammar of a node group's `zones` (2026-09-29-gpu-and-regional-app-pools R-002): a GCP
* zone name such as `us-east4-a`. Region family, a hyphen, a location word ending in digits, a
* hyphen, one zone letter.
*
* It is the same pattern the XRD constrains `spec.provisionedNodeTypes[].zones[]` with, and it is
* the injection guard for the same reason: WS-3's `_app-pool-node-groups.gotmpl` renders each zone
* into `nodeLocations` without quoting. No character a YAML or go-template break needs (a quote, a
* newline, a brace, a colon, a space) can match. WS-3's XRD item restates it by hand and the chart
* package's byte-mirror pins the two together. Anchored, no nested quantifier, linear on any input.
*/
const APP_POOL_NODE_GROUP_ZONE_PATTERN = /^[a-z]+-[a-z]+[0-9]+-[a-z]$/;
/**
* Most zones one node group may pin. A GCP region has three or four zones; 8 bounds the array
* above every region provisioning-svc can read from `provisionableEntriesForPlacement` with room
* to spare, so the bound never refuses a real region and still refuses an unbounded list.
*/
const APP_POOL_NODE_GROUP_ZONES_MAX = 8;
/**
* The accelerator a GPU node group's machines carry (2026-09-29-gpu-and-regional-app-pools D-3,
* DF-4). Present on an item exactly when the resolved machine type is GPU-backed; the operator's
* compiler copies it from the `resolveMachineType` resolution's `gpu`, which today it drops.
*
* Read by WS-3's fragment, which renders `guestAccelerator` (`type`, `count`),
* `gpuDriverInstallationConfig.gpuDriverVersion: LATEST` and the `cloud.google.com/gke-accelerator`
* label from it. GKE requires the accelerator declared on the node pool even for a G2 machine type
* whose name implies it, so an absent `gpu` on a GPU machine type renders nodes with no device.
*
* `acceleratorType` is a GCP accelerator name such as `nvidia-l4`, in
* `APP_POOL_LABEL_VALUE_PATTERN`'s grammar and at most 63 characters, because the template
* renders it unquoted into both the accelerator block and a label value. `count` is the whole
* number of accelerators per node, 1 to `MAX_GPU_COUNT` (8), the largest count any shipped GCP
* shape carries. The shape is `{ acceleratorType, count }` and nothing else (R-002): zones live on
* the item. `.strict()`; a conforming object parses to itself, anything else is a `ZodError`.
*/
const appPoolNodeGroupGpuSchema = z.object({
	acceleratorType: z.string().regex(APP_POOL_LABEL_VALUE_PATTERN).max(63),
	count: z.number().int().min(1).max(MAX_GPU_COUNT)
}).strict();
/**
* The node label that marks the nodes of a spot node group (2026-10-24-pool-machine-generations
* D-27, D-35, R-045): `zion.witness.ai/app-pool-capacity`, with the value `spot`.
*
* Who writes it: the Composition must stamp it, with the value `spot`, on every node of an item
* whose `capacityType` is `spot` (WS-3e, stage S3b). It stamps it on GKE and on EKS, beside the
* group label `zion.witness.ai/app-pool-group`. A node of an on-demand group carries no such label.
*
* Who reads it: the operator node census must read it to set `capacityType` on a census row
* (`liveNodePoolSchema` in `live-node-pool.ts`; WS-3c, stage S3c). The compositions test package
* must pin the rendered literal against this constant (WS-3e). Until both land, no node carries the
* label and no census row carries `capacityType`.
*
* Why a platform label and not the vendor label: the platform owns this key on both clouds, so one
* reader serves GKE and EKS. The value is a Kubernetes label key: a DNS prefix, one slash and a name,
* 33 characters. It never changes once shipped, because live nodes carry it.
*/
const APP_POOL_CAPACITY_LABEL = "zion.witness.ai/app-pool-capacity";
/** `appKey` cap: the app-key budget the platform's own doors use. An `orgId` is a 36-character UUID
*  and is bounded by its own pattern. */
const APP_POOL_NODE_GROUP_KEY_MAX_LENGTH = 63;
/** `groupId` cap: `apg-` plus 20 hex. Stated as a number as well as pinned by the pattern, because
*  WS-7's XRD carries a `maxLength` that has to mirror something. */
const APP_POOL_NODE_GROUP_ID_MAX_LENGTH = 24;
/** The message of the refine that binds an item's `tier` to its machine. */
const APP_POOL_NODE_GROUP_TIER_MISMATCH_MESSAGE = "tier must name the family of nodeType, or the accelerator model and the gpu count of gpu on a family of nodeType that carries that model, on the tier cloud";
/** The message of the check that refuses a tier key whose middle part is longer than any tier
*  `family` (`APP_POOL_TIER_FAMILY_MAX_LENGTH`, 20 characters). */
const APP_POOL_NODE_GROUP_TIER_FAMILY_MESSAGE = `the middle part of tier must be at most ${String(APP_POOL_TIER_FAMILY_MAX_LENGTH)} characters, the longest tier family`;
/** The message of the refine that binds an item's `capacityType` to the capacity part of its `tier`. */
const APP_POOL_NODE_GROUP_CAPACITY_MISMATCH_MESSAGE = "capacityType must be spot exactly when tier ends in .spot, and absent on a group with no tier";
/**
* Whether the middle part of a tier key, the text between its first and its second dot, is at most
* `APP_POOL_TIER_FAMILY_MAX_LENGTH` (20) characters (2026-10-24-pool-machine-generations D-26,
* DEF-1).
*
* Why: `appPoolTierKeySchema` bounds the whole key at 34 characters, so it admits a middle part of
* 25 characters behind `gcp.` and `.spot`. No declared tier gives such a key: a CPU tier's `family`
* is at most 20 characters, and the longest GPU middle part, `rtx-pro-6000x8`, is 14. R-009 keeps
* the shared schema as it is (WS-1b E-3), so this package refuses the longer middle part itself.
*
* Output: `true` when the middle part has at most 20 characters, a key with no dot included (its
* middle part is empty). Pure, total over any string, never throws.
*/
function tierKeyMiddleFits(key) {
	return (key.split(".")[1] ?? "").length <= APP_POOL_TIER_FAMILY_MAX_LENGTH;
}
/**
* The tier key grammar of a node group: `appPoolTierKeySchema`, plus {@link tierKeyMiddleFits}.
* The item member `tier` parses with it, and {@link isAppPoolNodeGroupTierKey} answers with it.
*/
const appPoolNodeGroupTierKeySchema = appPoolTierKeySchema.refine(tierKeyMiddleFits, { message: APP_POOL_NODE_GROUP_TIER_FAMILY_MESSAGE });
/**
* Whether `key` is a tier key that a group of a declared tier can carry
* (2026-10-24-pool-machine-generations D-26, DEF-1): it matches `appPoolTierKeySchema`, and its
* middle part is at most `APP_POOL_TIER_FAMILY_MAX_LENGTH` (20) characters.
*
* Read by {@link appPoolNodeGroupId}, which throws for any other key, by the item refine of
* {@link appPoolNodeGroupSpecSchema}, and by the group-id binding of the satellite run request and
* slice in `app-pool-subjects.ts`, which hashes only such keys. Not in the package barrel.
*
* Output: `true` for `gcp.c4d.spot` and `gcp.h100x8.on-demand`. `false` for `gcp.c4d`,
* `GCP.c4d.spot`, a key past 34 characters, and `gcp.` plus 25 letters plus `.spot`. Pure, never
* throws.
*/
function isAppPoolNodeGroupTierKey(key) {
	return appPoolNodeGroupTierKeySchema.safeParse(key).success;
}
/**
* One compiled and clamped app-pool node group. Every attempt of one `(appKey, orgId, workload,
* nodeType, region)` tuple lands on the nodes this group describes, and on no others. `region` is
* absent for a home group (2026-09-29-gpu-and-regional-app-pools D-15).
*
* Every string field carries an injection guard before it carries a name grammar (R-035, chain D
* hop 4). That is what puts the grammar on the wire schema as a `regex`, instead of somewhere
* downstream. WS-7's `_app-pool-node-groups.gotmpl` renders `groupId`, `nodeType` and `diskType`
* into YAML without quoting; `appKey` and `orgId` are hash inputs and log fields only. `appKey` and
* `nodeType` carry `APP_POOL_LABEL_VALUE_PATTERN` from `@zion/sdk-contracts`, the one label-value
* grammar, which keeps the hash material and the log lines free of a quote, a newline or a `{{`
* and is the grammar a Kubernetes label value admits. It admits neither a leading nor a trailing
* hyphen: `^[a-z]([a-z0-9-]*)?$`, the shape a prior revision of the isolated spec carried, admits
* `a-`, which every Kubernetes label door refuses at admission, where a reconcile loop records a
* failure and continues.
*
* Produced by WS-4's `app-pool-compiler.ts`, one element per distinct tuple, packed. A group is
* not per pool and not per run. Three consumers read it. WS-3 persists it, then recomputes and
* PATCHes it. WS-4's `app-pool-placement.ts` reads `groupId` for the pod's `nodeSelector` and
* toleration. WS-7's Composition renders one `NodePool` per element. The XRD's
* `spec.appPoolNodeGroups` items byte-mirror this shape, pinned by `xrd-handler-shape.spec.ts`.
*
* Inputs and ranges are documented per field below. A conforming object parses to itself. Anything
* else is a `ZodError` naming the field. `StatusReportSchema` is `.strict()` end to end, so a
* rejection there fails the whole report, and {@link APP_POOL_NODE_GROUP_MAX} exists to prevent
* that. Parsing is pure and has no side effects.
*
* `.strict()` makes an unknown key a parse error, so a field the CP does not name can never reach
* the XR. `nodeType` holds the resolved machine type. It is not the manifest field of that name.
*/
const appPoolNodeGroupSpecSchema = z.object({
	/** `apg-<20 hex>`, {@link appPoolNodeGroupId}'s output. The compiler never invents one. */
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN).max(APP_POOL_NODE_GROUP_ID_MAX_LENGTH),
	/** The app this group belongs to. One group never carries the pods of two apps (chain G hop 3). */
	appKey: z.string().regex(APP_POOL_LABEL_VALUE_PATTERN).max(APP_POOL_NODE_GROUP_KEY_MAX_LENGTH),
	/**
	* The org whose attempts run here: the lowercase org UUID `APP_POOL_ORG_ID_PATTERN` admits (R-004),
	* the same string the subject segment and the submit door carry.
	*
	* A string for `out-of-mesh` and `null` for `in-mesh`, pinned in both directions by the first
	* `.refine` below. This is chain D at the schema. An `out-of-mesh` group with no org is a node
	* pool two tenants could share. An `in-mesh` group that carries an org is one node pool per
	* tenant for the app's own code, which buys no separation and costs a GKE pool per resident
	* org.
	*
	* There is one form and no converter. `canonicaliseOrgId` lowercases and validates the UUID
	* upstream. The DP that computes the id and the CP that recomputes it both hash this string
	* (ARCH-1). A UUID is a legal Kubernetes label value, so the XRD carries the same grammar.
	*/
	orgId: z.string().regex(APP_POOL_ORG_ID_PATTERN).nullable(),
	/** Which containment this group's nodes are for. Decides whether `orgId` is set at all. */
	workload: z.enum(APP_POOL_WORKLOADS),
	/** The resolved machine type. Pattern-guarded because the go-template renders it unquoted. */
	nodeType: z.string().regex(APP_POOL_LABEL_VALUE_PATTERN).max(APP_POOL_NODE_TYPE_MAX_LENGTH),
	/**
	* Warm floor, `0` to `POOL_NODE_COUNT_MAX`.
	*
	* A total across all zones (I-10), and never per zone. WS-7 renders it as
	* `autoscaling.totalMinNodeCount`. The isolated array carried the per-zone reading for months
	* while its docblock said otherwise, and every published figure was three times optimistic on
	* a three-zone pool. This array is a total from its first commit. Do not divide by a zone
	* count to reach it: the count is not knowable in the Composition and is not uniform across
	* the pools of one cluster.
	*/
	minNodeCount: z.number().int().min(0).max(POOL_NODE_COUNT_MAX),
	/**
	* Ceiling, `1` to `POOL_NODE_COUNT_MAX`, and also a total across all zones (I-10).
	*
	* The floor of 1 matters: a 0/0 group is never emitted. The pool is refused at compile instead,
	* so an emitted group always names capacity something could run on.
	*/
	maxNodeCount: z.number().int().min(1).max(POOL_NODE_COUNT_MAX),
	/**
	* Boot disk type. CP-computed, and never report-supplied. provisioning-svc overwrites it on
	* every read with `bootDiskTypeForMachineType(nodeType)`, which is chain D hop 3 and the
	* spread-and-overwrite pattern its dedicated twin already implements. A DP-side operator
	* therefore cannot choose its own disk SKU by putting a value in a status report.
	*
	* Optional on the wire because pre-change XRs lack it; the CP always sets it, the XRD requires it and the fragment carries no default (ARCH-21).
	*/
	diskType: z.enum(BOOT_DISK_TYPES).optional(),
	/**
	* Pod density, one of {@link APP_POOL_NODE_GROUP_MAX_PODS_PER_NODE_VALUES}. CP-computed, and
	* never report-supplied, on the same reasoning as `diskType`: without the overwrite a DP-side
	* operator could widen its own pod-address budget from a status report.
	*
	* The `.meta({ type: 'integer' })` call is emitter metadata and not validation, and it has to
	* stay. `z.literal([32, 64])` on its own emits `"type": "number"`, so a generated OpenAPI
	* client models a whole-number field as a float, while both direct siblings `minNodeCount` and
	* `maxNodeCount` emit `"type": "integer"`. The `.meta()` call overrides the emitted type and
	* changes nothing else; runtime validation is identical with and without it. Do not replace
	* this with `z.number().int()` to fix the emitted type. That form emits `integer` and drops
	* the enum, and the closed set is the reason the constant exists.
	*
	* The field is immutable on a live GKE pool, so a change to it takes effect only through a
	* `groupId` recreate, and never in place.
	*/
	maxPodsPerNode: z.literal(APP_POOL_NODE_GROUP_MAX_PODS_PER_NODE_VALUES).meta({ type: "integer" }).optional(),
	/**
	* The group's accelerator, present exactly when `nodeType` is GPU-backed
	* (2026-09-29-gpu-and-regional-app-pools D-3). Written by the operator's compiler from the
	* machine-type resolution. See {@link appPoolNodeGroupGpuSchema}.
	*
	* Not part of the group identity: the accelerator is a function of `nodeType`, which the id
	* already hashes, so adding it would change no id and name nothing new (D-15).
	*/
	gpu: appPoolNodeGroupGpuSchema.optional(),
	/**
	* The zones the group's node pool is pinned to, rendered as `nodeLocations` (R-002). Filled by
	* provisioning-svc at patch time from `provisionableEntriesForPlacement(region, 'gcp')` for this
	* item's `nodeType`, in the item's `region` or the data plane's own. The operator never writes
	* it, so a status report's items carry none.
	*
	* Absent means the pool inherits every zone of its cluster, which is every group before this
	* field existed. Present, it holds 1 to {@link APP_POOL_NODE_GROUP_ZONES_MAX} distinct zones
	* in {@link APP_POOL_NODE_GROUP_ZONE_PATTERN}'s grammar, all in one region (a refine below), and
	* when `region` is set, in that region (another refine). A node pool's zones must be in its
	* cluster's region, and GKE refuses the pool otherwise, so a mismatch is refused here where the
	* field is named. A home group's region is not known to this schema, so its zones need only
	* agree with each other.
	*
	* Not part of the group identity: zones are CP-filled per patch, and a zone list change must
	* move the node pool's locations and never its id.
	*/
	zones: z.array(z.string().regex(APP_POOL_NODE_GROUP_ZONE_PATTERN)).min(1).max(8).refine((zs) => new Set(zs).size === zs.length, { message: "zones must be distinct" }).optional(),
	/**
	* The satellite region the group's node pool runs in (2026-09-29-gpu-and-regional-app-pools
	* D-4, D-5), in `APP_POOL_REGION_PATTERN`'s grammar (DF-1). Absent means the data plane's home
	* cluster, which is every group before this field existed.
	*
	* Present, WS-4's Composition renders the node pool against the satellite cluster of that
	* region, and {@link appPoolNodeGroupId} hashes the region, so a regional group and a home
	* group of one tuple are two node pools. A group names a region only for an out-of-mesh pool,
	* because the publish rule `pool-region-requires-out-of-mesh` refuses the in-mesh pairing
	* upstream; this schema does not repeat that rule.
	*/
	region: appPoolRegionSchema.optional(),
	/**
	* The boot disk size of every node in the group, in GiB (2026-10-02-regional-pools-release-2
	* D-11, DF-1, DF-6). A whole number from 10 to 4096, the bounds `SCALING_INTENT_DISK_GIB_MIN`
	* and `SCALING_INTENT_DISK_GIB_MAX` that the dedicated twin's `diskSizeGb` reads. Absent means
	* the fragment's own default, which is every group before this field existed.
	*
	* Written by the operator's compiler (WS-9) from the pool's `diskSizeGb`, and the compiler's
	* packing key holds it, so two pools of one tuple with two disk sizes compile to two groups.
	* provisioning-svc passes it through to the XR (WS-4). The Composition renders it as
	* `nodeConfig.diskSizeGb`, else 100 (WS-3).
	*
	* Part of the group identity when present: GKE does not change a boot disk size in place, so a
	* new size is a new node pool. {@link appPoolNodeGroupId} hashes it only when it is set, so a
	* group without it keeps its shipped id.
	*/
	diskSizeGb: z.int().min(SCALING_INTENT_DISK_GIB_MIN).max(SCALING_INTENT_DISK_GIB_MAX).optional(),
	/**
	* The tier key of the group (2026-10-24-pool-machine-generations D-16, D-26): the output of
	* `appPoolTierKey` from `@zion/sdk-contracts`, such as `gcp.c4d.spot` or
	* `gcp.h100x8.on-demand`, in `appPoolTierKeySchema`'s grammar (at most 34 characters), with a
	* middle part of at most 20 characters, the longest tier `family` (see
	* {@link isAppPoolNodeGroupTierKey}).
	*
	* Absent on a group of a pool with `class`, which is every group before this field existed,
	* so such a group keeps its shipped id (DEF-8). The S2b rows of the plan are the writers and
	* readers of this member: the operator's compiler (WS-2b) is to write it for a group of a tier
	* that the pool declares in `tiers`; provisioning-svc (WS-2d) and site-admin-api (WS-2e) are to
	* pass it to {@link appPoolNodeGroupId} when they re-derive the id; the XRD item (WS-2f) is to
	* declare it. This package holds none of these callers. The satellite run request and slice
	* in `app-pool-subjects.ts` carry no tier member: they bind a group id to the tierless id of
	* the request's machine or to the key of a declared tier of that machine.
	*
	* Part of the group identity when present (D-26): a spot tier and an on-demand tier of one
	* machine type are two node pools. {@link appPoolNodeGroupId} hashes it only when it is set.
	*
	* Bound to the group's machine by a refine below: the key's middle part names the family of
	* `nodeType` on the key's cloud for a group with no `gpu`, and `<model>x<gpu.count>` of a model
	* whose accelerator table entry on that cloud holds `gpu.acceleratorType` for a group with
	* `gpu`. So an item cannot carry the key of one tier and the machine of another.
	*/
	tier: appPoolNodeGroupTierKeySchema.optional(),
	/**
	* The capacity of the group's nodes (2026-10-24-pool-machine-generations D-27, D-36): the
	* literal `spot` (`appPoolSpotCapacitySchema` of `@zion/sdk-contracts`), or absent for
	* on-demand nodes. `on-demand` and `null` are parse errors: the absent member is the on-demand
	* form, so one fact has one spelling (DEF-1).
	*
	* Who writes it: the operator compiler must set it on a group of a spot tier (WS-3c, stage
	* S3c). Who reads it: the Composition must render `spot: true` on GKE and `capacityType: SPOT`
	* on EKS for it, and stamp {@link APP_POOL_CAPACITY_LABEL} (WS-3e, stage S3b). provisioning-svc
	* must price the group at the spot rate (WS-3f, stage S3d; D-36).
	*
	* Bound to `tier` by a refine below: `spot` is present exactly when the item's `tier` ends in
	* `.spot`, and absent on a group with no `tier`. The tier key and this member state one fact
	* twice, and the refine holds them equal. So the price and the render cannot disagree with the
	* group id.
	*
	* The writer order. The refine refuses a group of a `.spot` tier with no `capacityType`.
	* `desiredAppPoolNodeGroups` in provisioning-svc parses each reported item with this schema at
	* its step 1a. It drops a refused item there, before its step 4 refuses a spot group as
	* `unpriced-node-type`. No shipped operator reports such a group: the S2 operator drops every
	* spot tier as `not-enabled` (`app-pool-tiers.ts`), so it compiles no spot group. So the
	* operator compiler must set `capacityType: 'spot'` in the same change that compiles a spot
	* group (WS-3c, stage S3c). Each provisioning-svc fixture of a spot group must carry
	* `capacityType: 'spot'` as well (WS-3f, stage S3d).
	*
	* Not part of the group identity (D-26): the tier key already parts a spot group from an
	* on-demand group of one machine type, so {@link appPoolNodeGroupId} hashes no new field, and
	* no live group id moves (DEF-8).
	*/
	capacityType: appPoolSpotCapacitySchema.optional()
}).strict().refine((g) => g.workload === "out-of-mesh" === (g.orgId !== null), {
	message: "orgId is required for out-of-mesh and forbidden for in-mesh",
	path: ["orgId"]
}).refine((g) => g.minNodeCount <= g.maxNodeCount, {
	message: "minNodeCount exceeds maxNodeCount",
	path: ["minNodeCount"]
}).refine((g) => zonesLieInRegion(g.zones, g.region), {
	message: "every zone must lie in the group region",
	path: ["zones"]
}).refine((g) => zonesShareOneRegion(g.zones), {
	message: "every zone must lie in one region",
	path: ["zones"]
}).refine((g) => tierFitsGroupMachine(g.tier, g.nodeType, g.gpu), {
	message: APP_POOL_NODE_GROUP_TIER_MISMATCH_MESSAGE,
	path: ["tier"]
}).refine((g) => capacityFitsTier(g.tier, g.capacityType), {
	message: APP_POOL_NODE_GROUP_CAPACITY_MISMATCH_MESSAGE,
	path: ["capacityType"]
});
/**
* The capacity part of a tier key: the text after its second dot, so `gcp.c4d.spot` gives `spot`
* and `gcp.h100x8.on-demand` gives `on-demand`. Read only on a key that
* {@link isAppPoolNodeGroupTierKey} admits, which always has three dot parts. Pure.
*/
function tierCapacityOf(tier) {
	return tier.split(".")[2];
}
/**
* Whether an item's `capacityType` agrees with its `tier` (2026-10-24-pool-machine-generations
* D-26, D-27, DEF-1).
*
* Output per input class:
* - no `tier`: `true` when `capacityType` is absent. A group of a class pool is on-demand;
* - a `tier` that {@link isAppPoolNodeGroupTierKey} refuses: `true`. The member's own issue names
*   it, and one fault raises one issue, as in {@link tierFitsGroupMachine};
* - a `tier` that ends in `.spot`: `true` when `capacityType` is `spot`;
* - any other admitted `tier`: `true` when `capacityType` is absent.
*
* A `capacityType` outside `appPoolSpotCapacitySchema` never reaches this function: zod skips the
* object refines after a type issue on a member. Read by the item refine of
* {@link appPoolNodeGroupSpecSchema}. Not exported. Pure, never throws.
*/
function capacityFitsTier(tier, capacityType) {
	if (tier === void 0) return capacityType === void 0;
	if (!isAppPoolNodeGroupTierKey(tier)) return true;
	return tierCapacityOf(tier) === "spot" === (capacityType === "spot");
}
/**
* The machine family of `nodeType` on one cloud (2026-10-24-pool-machine-generations R-008):
* `gcpMachineTypeFamily` for `gcp` (`c4d-standard-8` gives `c4d`) and `awsMachineTypeFamily` for
* `aws` (`m7i-flex-large` gives `m7i-flex`). These are the two family readers of
* `@zion/sdk-contracts`; a tier's `family` is spelled in the same text.
*
* Read by {@link tierFitsGroupMachine}, which the item refine of {@link appPoolNodeGroupSpecSchema}
* reads, and by `tierNamesMachine` in `app-pool-subjects.ts`, which the CPU tier refines of the
* satellite run request and slice view read. Not in the package barrel. Pure, total over any
* string, never throws.
*/
function machineTypeFamilyOnCloud(cloud, nodeType) {
	return cloud === "aws" ? awsMachineTypeFamily(nodeType) : gcpMachineTypeFamily(nodeType);
}
/**
* Whether an item's tier key names the item's own machine (D-26, DEF-1).
*
* Output per input class:
* - no `tier`: `true`. A group of a class pool carries no key;
* - a `tier` that {@link isAppPoolNodeGroupTierKey} refuses: `true`. The member's own issue names
*   it, and one fault raises one issue;
* - a group with no `gpu`: `true` when the key's middle part equals
*   {@link machineTypeFamilyOnCloud} of the key's cloud and `nodeType`;
* - a group with `gpu`: `true` when one model of `ACCELERATOR_MODELS` gives the middle part
*   `<model>x<gpu.count>`, `acceleratorTypesFor(model, cloud, gpu.count)` holds
*   `gpu.acceleratorType`, and the `families` of `acceleratorEntry(model, cloud)` hold the family
*   of `nodeType` on the key's cloud. So `g2-standard-4` carries an `l4` key, and
*   `c4d-standard-8` with an L4 `gpu` carries no GPU key. A model with no table entry on the key's
*   cloud gives no type and no family, so no GPU group of that cloud carries its key (the AWS rows
*   land in S4, WS-4a).
*
* Read by the item refine of {@link appPoolNodeGroupSpecSchema}, and by `tierNamesMachine` in
* `app-pool-subjects.ts`, which the GPU node refines and the group-id binding of the satellite run
* request and slice read. Not in the package barrel. Pure, never throws.
*/
function tierFitsGroupMachine(tier, nodeType, gpu) {
	if (tier === void 0 || !isAppPoolNodeGroupTierKey(tier)) return true;
	const [cloud, middle] = tier.split(".");
	const family = machineTypeFamilyOnCloud(cloud, nodeType);
	if (gpu === void 0) return family === middle;
	return ACCELERATOR_MODELS.some((model) => {
		const entry = acceleratorEntry(model, cloud);
		return `${model}x${gpu.count}` === middle && entry !== null && entry.families.includes(family) && acceleratorTypesFor(model, cloud, gpu.count).includes(gpu.acceleratorType);
	});
}
/** The region a zone lies in: the zone cut at its last hyphen, so `us-east4-a` gives `us-east4`. */
function zoneRegion(zone) {
	return zone.slice(0, zone.lastIndexOf("-"));
}
/**
* Whether every zone of a regional group lies in its region: `us-east4-a` lies in `us-east4`, and
* `us-east4a-b` does not. A zone is `<region>-<letter>`, so the test cuts the zone at its last
* hyphen and compares the head to the region. With no `region` or no `zones` there is nothing to compare and the answer is `true`:
* the home region is not known to this schema.
*/
function zonesLieInRegion(zones, region) {
	if (zones === void 0 || region === void 0) return true;
	return zones.every((zone) => zoneRegion(zone) === region);
}
/**
* Whether every zone lies in the same region, with or without a `region` member:
* `['us-east4-a', 'europe-west4-b']` does not. With no `zones` the answer is `true`. This is the
* rule that covers a home group, whose region the schema cannot see (round 1 architecture-4).
*/
function zonesShareOneRegion(zones) {
	return zones === void 0 || new Set(zones.map(zoneRegion)).size === 1;
}
/** The separator between the hash's four, five, six or seven fields. See {@link appPoolNodeGroupId} on why it is the
*  NUL byte. Built from its code point instead of typed as an escape, so this file stays
*  greppable. */
const NODE_GROUP_ID_FIELD_SEPARATOR = String.fromCharCode(0);
/** How many hex characters of the digest the id carries. 20, so `apg-` plus 20 gives 24
*  characters, inside GKE's 40-character `nodePoolId` budget with room for the Composition's own
*  object naming. */
const NODE_GROUP_ID_HEX_LENGTH = 20;
/**
* The identity function both planes agree on. The id is `'apg-'` followed by
* `sha256(appKey NUL orgId-or-empty NUL workload NUL nodeType)` in hex, sliced to 20 characters,
* and for a regional group `sha256(appKey NUL orgId-or-empty NUL workload NUL nodeType NUL region)`
* (2026-09-29-gpu-and-regional-app-pools D-15, DF-6). A group without `region` therefore keeps the
* id it had before the field existed, and its live node pool is not re-created on the release.
*
* A group with `diskSizeGb` hashes six fields: the four, then the region or the empty string, then
* the disk size in decimal (2026-10-02-regional-pools-release-2 D-11, DF-6). So
* `sha256(appKey NUL orgId-or-empty NUL workload NUL nodeType NUL region-or-empty NUL diskSizeGb)`.
* The region slot holds the empty string on a home group, for the reason the org slot does: an
* omitted slot would let a disk size stand where a region stands. A group without `diskSizeGb`
* hashes the four or five fields it hashed before the member existed.
*
* Called by WS-4's `app-pool-compiler.ts`, the only producer, and by WS-3, which recomputes an id
* to verify a reported group. Pure, with no I/O and no side effects. The hash is `node:crypto`'s
* `createHash('sha256')`.
*
* Input is an {@link AppPoolNodeGroupIdentity}. This function does not validate the field
* grammars. {@link appPoolNodeGroupSpecSchema} does that on the value the id goes into. A caller
* that hashes an ungrammatical `appKey` therefore gets a well-formed id for a spec that is then
* refused. That order is correct, because the id is not where a name is policed.
*
* Output per input class:
*  - `in-mesh` with `orgId: null` gives a 24-character id matching
*    {@link APP_POOL_NODE_GROUP_ID_PATTERN}, with an empty string in the org position, so the org
*    contributes nothing.
*  - `out-of-mesh` with an org UUID gives an id that changes with the org and with nothing else
*    held equal.
*  - Equal identities give equal ids, on every process and every release. This is a wire identity:
*    it is recomputed on both planes and compared, so it may never change once shipped.
*
* Errors. A `RangeError` when `region` is the empty string: the id would differ from the home
* group's while naming no region, and the schema refuses the value anyway. A `RangeError` when a
* present `region` is outside `APP_POOL_REGION_PATTERN`: `'US-EAST4'` would hash to an id that is
* not `'us-east4'`'s, and a region carrying the NUL byte would forge a field boundary. A `RangeError`
* when a present `diskSizeGb` is not a whole number from 10 to 4096: `200.5` or `NaN` would hash to
* an id no schema-valid group carries. A `RangeError` on an
* incoherent `(workload, orgId)` pair, meaning `out-of-mesh` with
* `orgId: null` or `in-mesh` with a string org. It throws instead of picking a reading, because
* both plausible repairs are wrong in a direction nothing downstream can see. Defaulting an absent
* org to the empty string would give two tenants one node pool, and chain D collapses. Ignoring an
* org on `in-mesh` would give the id a field the schema says is absent. The CP's recompute step
* would then disagree with the DP's compile, and the group would never be PATCHed. The result is a
* pool that provisions nothing, with nothing red anywhere. A throw is a caller bug on a code path
* the compiler owns. It surfaces at compile time on the operator instead of at admission.
*
* Invariants:
*  - A home group and a regional group never share material: a four-field material carries three
*    separators and a five-field one four. The region cannot hold a separator, because this
*    function refuses a region outside `APP_POOL_REGION_PATTERN`.
*  - A group with a disk size never shares material with one without: a six-field material
*    carries five separators. The disk size is decimal digits only, so it holds no separator.
*  - Injective over the four fields, by NUL separation. Without it, `{appKey:'a', orgId:'bc'}` and
*    `{appKey:'ab', orgId:'c'}` would concatenate to the same bytes and share a node pool. The NUL
*    byte is the separator because {@link appPoolNodeGroupSpecSchema}'s grammars cannot produce
*    one, so no field value can forge a boundary. The isolated and dedicated compilers separate
*    their inputs the same way.
*  - The org appears in the digest if and only if `workload` is `'out-of-mesh'`.
*  - A group with a tier key never shares material with one without: a seven-field material
*    carries six separators. The key holds no separator, because this function refuses a key
*    that {@link isAppPoolNodeGroupTierKey} refuses.
*
* A group with `tier` hashes seven fields (2026-10-24-pool-machine-generations D-16, D-26): the
* four, then the region or the empty string, then the disk size in decimal or the empty string,
* then the tier key. So `sha256(appKey NUL orgId-or-empty NUL workload NUL nodeType NUL
* region-or-empty NUL diskSizeGb-or-empty NUL tier)`. The two empty slots stand for the same reason
* the region slot does on a six-field group: an omitted slot would let a tier key stand where a
* region or a disk size stands. A group without `tier` hashes the four, five or six fields it
* hashed before the member existed, so no live group id moves (DEF-8). A spot tier and an
* on-demand tier of one machine type hash two keys, so they get two ids and two node pools.
*
* A `RangeError` when a present `tier` is a key that {@link isAppPoolNodeGroupTierKey} refuses: the
* empty string, a key outside `appPoolTierKeySchema` (`gcp.c4d`, `GCP.c4d.spot`, a key past 34
* characters), or a key whose middle part is past 20 characters (`gcp.` plus 25 letters plus
* `.spot`). The id would name no tier any schema-valid group carries, and a key with the NUL byte
* would forge a field boundary.
*/
function appPoolNodeGroupId(identity) {
	const { appKey, orgId, workload, nodeType, region, diskSizeGb, tier } = identity;
	if (region === "") throw new RangeError("appPoolNodeGroupId: region must be a region name or absent, got the empty string. An empty region would hash to an id that is neither the home group nor any regional group.");
	if (region !== void 0 && !APP_POOL_REGION_PATTERN.test(region)) throw new RangeError(`appPoolNodeGroupId: region must match APP_POOL_REGION_PATTERN, got ${JSON.stringify(region)}`);
	if (diskSizeGb !== void 0 && !(Number.isInteger(diskSizeGb) && diskSizeGb >= SCALING_INTENT_DISK_GIB_MIN && diskSizeGb <= SCALING_INTENT_DISK_GIB_MAX)) throw new RangeError(`appPoolNodeGroupId: diskSizeGb must be a whole number from ${SCALING_INTENT_DISK_GIB_MIN} to ${SCALING_INTENT_DISK_GIB_MAX}, got ${String(diskSizeGb)}`);
	if (tier !== void 0 && !isAppPoolNodeGroupTierKey(tier)) throw new RangeError(`appPoolNodeGroupId: tier must be a tier key such as gcp.c4d.spot or absent, got ${JSON.stringify(tier)}`);
	if (workload === "out-of-mesh" !== (orgId !== null)) throw new RangeError(`appPoolNodeGroupId: workload '${workload}' requires orgId to be ${workload === "out-of-mesh" ? "a string" : "null"}, got ${orgId === null ? "null" : `'${orgId}'`}. An out-of-mesh group without an org would carry two tenants' attempts on one node pool; an in-mesh group with one would disagree with the spec the CP verifies against.`);
	const fields = [
		appKey,
		orgId ?? "",
		workload,
		nodeType
	];
	if (region !== void 0 || diskSizeGb !== void 0 || tier !== void 0) fields.push(region ?? "");
	if (diskSizeGb !== void 0 || tier !== void 0) fields.push(diskSizeGb === void 0 ? "" : String(diskSizeGb));
	if (tier !== void 0) fields.push(tier);
	const material = fields.join(NODE_GROUP_ID_FIELD_SEPARATOR);
	return `apg-${createHash("sha256").update(material).digest("hex").slice(0, NODE_GROUP_ID_HEX_LENGTH)}`;
}
/**
* The whole array as it rides `StatusReport.appPoolNodeGroups` and the XR's
* `spec.appPoolNodeGroups`, capped at {@link APP_POOL_NODE_GROUP_MAX} items.
*
* Produced by WS-4, parsed by WS-3 on the way in and by WS-7's XRD on the way out. An array of at
* most 100 conforming items parses to itself. A longer array, or one bad item, is a `ZodError`,
* and because the enclosing status report is `.strict()` that rejection fails the whole report.
*
* An empty array is a valid and meaningful value: it authorizes a delete. Absence is the different
* answer, that this operator has not compiled yet, and `StatusReport.appPoolNodeGroups` carries
* the `!== undefined` guard that keeps the two apart. Pure.
*/
const appPoolNodeGroupsSchema = z.array(appPoolNodeGroupSpecSchema).max(100);
//#endregion
export { APP_POOL_CAPACITY_LABEL, APP_POOL_MAX_PODS_PER_NODE_DEFAULT, APP_POOL_NODE_GROUP_ID_PATTERN, APP_POOL_NODE_GROUP_MAX, APP_POOL_NODE_GROUP_MAX_PODS_PER_NODE_VALUES, APP_POOL_NODE_GROUP_ZONES_MAX, APP_POOL_NODE_GROUP_ZONE_PATTERN, appPoolNodeGroupGpuSchema, appPoolNodeGroupId, appPoolNodeGroupSpecSchema, appPoolNodeGroupsSchema, isAppPoolNodeGroupTierKey, machineTypeFamilyOnCloud, tierFitsGroupMachine };

//# sourceMappingURL=app-pool-node-group.js.map